import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    SelectChangeEvent,
    Box,
    Typography,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { LeaveService } from '../../services/leave.service';
import { DocumentRequirement, LeaveTypeConfig, UserLeaveBalanceSummary } from '../../interfaces/leave.interface';
import { LeaveTypeService } from '../../services/leave-type.service';
import { LeaveBalanceService } from '../../services/leave-balance.service';
import { format, differenceInCalendarDays } from 'date-fns';
import { UserProfileService } from '../../services/user-profile.service';
import { Gender } from '../../interfaces/user-profile.interface';
import { useToast } from '../../context/ToastContext';
import { TEXT_AREA_MAX_LENGTH } from '../../constants/fieldLimits';

import SearchableSelect from '../common/SearchableSelect';
interface LeaveFormProps {
    open: boolean;
    onClose: () => void;
    onSubmitSuccess: () => void;
}

const LeaveForm: React.FC<LeaveFormProps> = ({ open, onClose, onSubmitSuccess }) => {
    const defaultDate = format(new Date(), 'yyyy-MM-dd');
    const [allLeaveTypes, setLeaveTypes] = useState<LeaveTypeConfig[]>([]);
    const [applicantGender, setApplicantGender] = useState<Gender | null>(null);
    // Maternity-shaped types (paid + minimum tenure) are not offered to male applicants. This is a
    // display filter only; the server still enforces eligibility.
    const leaveTypes = applicantGender === Gender.MALE
        ? allLeaveTypes.filter(lt => !(lt.isPaid === true && lt.minimumTenureMonths != null))
        : allLeaveTypes;
    const [formData, setFormData] = useState({
        leaveType: '',
        leaveReason: '',
        startDate: defaultDate,
        endDate: defaultDate,
        institutionId: '',
    });
    const [files, setFiles] = useState<File[]>([]);
    const [balanceSummary, setBalanceSummary] = useState<UserLeaveBalanceSummary | null>(null);
    const toast = useToast();

    // Keep a valid leave type selected (also after the maternity type is filtered out for men).
    useEffect(() => {
        if (leaveTypes.length > 0 && !leaveTypes.some(lt => lt.name === formData.leaveType)) {
            setFormData(prev => ({ ...prev, leaveType: leaveTypes[0].name }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allLeaveTypes, applicantGender]);

    const selectedLeaveType = leaveTypes.find(lt => lt.name === formData.leaveType);
    const docRequirement = selectedLeaveType?.documentRequirement || DocumentRequirement.NONE;

    const isDateRangeInvalid = !!(formData.startDate && formData.endDate
        && new Date(formData.endDate) < new Date(formData.startDate));

    const numberOfDays = formData.startDate && formData.endDate && !isDateRangeInvalid
        ? differenceInCalendarDays(new Date(formData.endDate), new Date(formData.startDate)) + 1
        : 0;

    const selectedBalance = balanceSummary?.balances.find(
        b => b.leaveType.toLowerCase() === formData.leaveType.toLowerCase()
    );

    useEffect(() => {
        LeaveTypeService.getByInstitution(true).then((types) => {
            setLeaveTypes(types);
        }).catch(() => {});
        UserProfileService.getLoggedInUserProfile()
            .then(profile => setApplicantGender(profile.gender ?? null))
            .catch(() => {});
        LeaveBalanceService.getMyBalance().then(setBalanceSummary).catch(() => {});
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (name === 'leaveType') {
            setFiles([]);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
        }
        e.target.value = '';
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (new Date(formData.endDate) < new Date(formData.startDate)) {
            toast.showError('End date cannot be before start date');
            return;
        }

        if (selectedBalance && numberOfDays > selectedBalance.remaining) {
            toast.showError(
                `Insufficient ${formData.leaveType} balance. Remaining: ${selectedBalance.remaining} day(s), Requested: ${numberOfDays} day(s)`
            );
            return;
        }

        if (docRequirement === DocumentRequirement.MANDATORY && files.length === 0) {
            toast.showError('Documents are required for this leave type');
            return;
        }

        try {
            const submitData = {
                ...formData,
                startDate: new Date(formData.startDate).toISOString(),
                endDate: new Date(formData.endDate).toISOString()
            };
            await LeaveService.createLeaveApplication(submitData, files.length > 0 ? files : undefined);
            toast.showSuccess('Leave application submitted');
            onSubmitSuccess();
            onClose();
            setFiles([]);
        } catch (err) {
            toast.showApiError(err, 'Failed to submit leave application');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'leave.applyForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>Apply for Leave</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent data-help-section="leave.applyForm.section.details">
                    <SearchableSelect
                        data-help="leave.applyForm.field.leaveType"
                        options={leaveTypes}
                        value={formData.leaveType}
                        onChange={(name) => { setFormData(prev => ({ ...prev, leaveType: name })); setFiles([]); }}
                        getOptionId={(lt) => lt.name}
                        getOptionLabel={(lt) => lt.name}
                        label="Leave Type"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        data-help="leave.applyForm.field.startDate"
                        label="Start Date"
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2 }}
                        required
                    />

                    <TextField
                        fullWidth
                        data-help="leave.applyForm.field.endDate"
                        label="End Date"
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: formData.startDate }}
                        error={isDateRangeInvalid}
                        helperText={isDateRangeInvalid ? 'End date must be on or after start date' : ''}
                        sx={{ mb: 2 }}
                        required
                    />

                    {numberOfDays > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Chip
                                label={`${numberOfDays} day${numberOfDays !== 1 ? 's' : ''}`}
                                color="primary"
                                variant="outlined"
                            />
                            {selectedBalance && (
                                <Typography variant="body2" color={selectedBalance.remaining < numberOfDays ? 'error' : 'text.secondary'}>
                                    Remaining: {selectedBalance.remaining} day(s)
                                </Typography>
                            )}
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        data-help="leave.applyForm.field.reason"
                        label="Reason"
                        name="leaveReason"
                        value={formData.leaveReason}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        required
                        inputProps={{ maxLength: TEXT_AREA_MAX_LENGTH }}
                        helperText={`${(formData.leaveReason || '').length}/${TEXT_AREA_MAX_LENGTH}`}
                    />

                    {docRequirement !== DocumentRequirement.NONE && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                {docRequirement === DocumentRequirement.MANDATORY
                                    ? 'Supporting documents are required *'
                                    : 'You may attach supporting documents (optional)'}
                            </Typography>
                            <Button
                                data-help="leave.applyForm.attach"
                                variant="outlined"
                                component="label"
                                startIcon={<AttachFileIcon />}
                                size="small"
                            >
                                Attach Files
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    onChange={handleFileSelect}
                                />
                            </Button>
                            {files.length > 0 && (
                                <List dense sx={{ mt: 1 }}>
                                    {files.map((file, index) => (
                                        <ListItem key={index}>
                                            <ListItemText
                                                primary={file.name}
                                                secondary={`${(file.size / 1024).toFixed(1)} KB`}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton data-help="leave.applyForm.removeFile" edge="end" size="small" onClick={() => handleRemoveFile(index)} aria-label="remove file">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="leave.applyForm.cancel" onClick={onClose}>Cancel</Button>
                    <Button data-help="leave.applyForm.save" type="submit" variant="contained" color="primary" disabled={isDateRangeInvalid}>
                        Submit
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default LeaveForm;
